@if (loading) {
  <div class="d-flex justify-content-center">
    <ix-spinner size="large" class="mt-5"></ix-spinner>
  </div>
} @else {
  <div class="input-field-size-max">
    <div class="d-flex justify-content-between mt-4">
      <ix-typography format="h4" id="emailSenderHeader">
        {{ 'organizations.settings.email_sender.header' | translate }}
      </ix-typography>
      <ix-toggle
        id="emailSenderToggle"
        (checkedChange)="isCustomSenderEnabled = !isCustomSenderEnabled"
        [checked]="isCustomSenderEnabled"
      ></ix-toggle>
    </div>

    @if (!isCustomSenderEnabled) {
      <ix-empty-state
        class="mt-5"
        id="emailSenderEmptyState"
        icon="mail"
        header="{{ 'organizations.settings.email_sender.empty.header' | translate }}"
        subHeader="{{ 'organizations.settings.email_sender.empty.subheader' | translate }}"
      />
    } @else {
      <ix-typography format="body" class="d-block mt-2 text-soft">
        {{ 'organizations.settings.email_sender.description' | translate }}
      </ix-typography>

      @if (config) {
        <ix-message-bar
          id="emailSenderVerificationStatus"
          class="mt-3"
          [persistent]="true"
          [type]="isVerified ? 'success' : config.verificationStatus === 'failed' ? 'danger' : 'info'"
        >
          {{ 'organizations.settings.email_sender.verification.' + config.verificationStatus | translate }}
          @if (config.verificationMessage && !isVerified) {
            <br />
            <small>{{ config.verificationMessage }}</small>
          }
        </ix-message-bar>
      }

      @if (config?.lastError) {
        <ix-message-bar type="warning" [persistent]="true" class="mt-3" id="emailSenderLastError">
          {{
            'organizations.settings.email_sender.last_error'
              | translate: {date: (config?.lastErrorAt | date: 'short')}
          }}
          <br />
          <small>{{ config?.lastError }}</small>
        </ix-message-bar>
      }

      @for (errorMessage of errorMessages; track errorMessage) {
        <ix-event-list class="mt-3">
          <ix-event-list-item class="no-pointer-event" item-color="--theme-color-alarm">
            {{ errorMessage }}
          </ix-event-list-item>
        </ix-event-list>
      }

      <form [formGroup]="form" class="needs-validation" novalidate>
        <ix-typography format="h5" class="mt-4" id="emailSenderIdentityHeader">
          {{ 'organizations.settings.email_sender.identity_header' | translate }}
        </ix-typography>

        <ix-input
          id="emailSenderFromAddress"
          class="col-md-12 mt-2 input-field-size-max"
          type="email"
          formControlName="fromAddress"
          [label]="'organizations.settings.email_sender.from_address' | translate"
          [class.ix-invalid]="isInvalid('fromAddress')"
          [invalidText]="getErrorMessage('fromAddress') | translate"
        />
        <ix-input
          id="emailSenderFromName"
          class="col-md-12 mt-2 input-field-size-max"
          type="text"
          formControlName="fromName"
          [label]="'organizations.settings.email_sender.from_name' | translate"
          [class.ix-invalid]="isInvalid('fromName')"
          [invalidText]="getErrorMessage('fromName') | translate"
        />
        <ix-input
          id="emailSenderSupportEmail"
          class="col-md-12 mt-2 input-field-size-max"
          type="email"
          formControlName="supportEmail"
          [label]="'organizations.settings.email_sender.support_email' | translate"
          [helperText]="'organizations.settings.email_sender.support_email_hint' | translate"
          [class.ix-invalid]="isInvalid('supportEmail')"
          [invalidText]="getErrorMessage('supportEmail') | translate"
        />

        @if (hasProviderChoice) {
          <ix-typography format="h5" class="mt-4" id="emailSenderProviderHeader">
            {{ 'organizations.settings.email_sender.provider_header' | translate }}
          </ix-typography>

          <ix-select
            id="emailSenderProvider"
            class="col-md-12 mt-2 input-field-size-max"
            formControlName="provider"
            i18nSelectListHeader="{{ 'general.select_list_header' | translate }}"
            [label]="'organizations.settings.email_sender.provider' | translate"
          >
            @for (p of providers; track p) {
              <ix-select-item
                [label]="'organizations.settings.email_sender.providers.' + p | translate"
                [value]="p"
              />
            }
          </ix-select>
        } @else {
          <ix-typography format="h5" class="mt-4" id="emailSenderProviderHeader">
            {{ 'organizations.settings.email_sender.providers.' + provider | translate }}
          </ix-typography>
        }

        @if (provider === 'smtp') {
          <div formGroupName="smtp">
            <ix-input
              id="emailSenderSmtpHost"
              class="col-md-12 mt-2 input-field-size-max"
              type="text"
              formControlName="host"
              [label]="'organizations.settings.email_sender.smtp.host' | translate"
              [class.ix-invalid]="isInvalid('smtp.host')"
              [invalidText]="getErrorMessage('smtp.host') | translate"
            />
            <div class="d-flex gap-3">
              <ix-select
                id="emailSenderSmtpPort"
                class="col-md-6 mt-2"
                formControlName="port"
                i18nSelectListHeader="{{ 'general.select_list_header' | translate }}"
                [label]="'organizations.settings.email_sender.smtp.port' | translate"
              >
                @for (port of ports; track port) {
                  <ix-select-item [label]="'' + port" [value]="port" />
                }
              </ix-select>
              <ix-select
                id="emailSenderSmtpSecurity"
                class="col-md-6 mt-2"
                formControlName="security"
                i18nSelectListHeader="{{ 'general.select_list_header' | translate }}"
                [label]="'organizations.settings.email_sender.smtp.security' | translate"
              >
                @for (s of securities; track s) {
                  <ix-select-item
                    [label]="'organizations.settings.email_sender.smtp.securities.' + s | translate"
                    [value]="s"
                  />
                }
              </ix-select>
            </div>
            <ix-input
              id="emailSenderSmtpUsername"
              class="col-md-12 mt-2 input-field-size-max"
              type="text"
              formControlName="username"
              autocomplete="off"
              [label]="'organizations.settings.email_sender.smtp.username' | translate"
            />
            <ix-input
              id="emailSenderSmtpPassword"
              class="col-md-12 mt-2 input-field-size-max"
              type="password"
              formControlName="password"
              autocomplete="new-password"
              [label]="'organizations.settings.email_sender.smtp.password' | translate"
              [placeholder]="hasStoredPassword ? ('organizations.settings.email_sender.secret_stored' | translate) : ''"
              [helperText]="
                hasStoredPassword ? ('organizations.settings.email_sender.secret_keep_hint' | translate) : ''
              "
              [class.ix-invalid]="isInvalid('smtp.password')"
              [invalidText]="getErrorMessage('smtp.password') | translate"
            />
          </div>
        }

        @if (provider === 'ses') {
          <ix-typography format="body-sm" class="d-block mt-2 text-soft" id="emailSenderSesHint">
            {{ 'organizations.settings.email_sender.ses.hint' | translate }}
          </ix-typography>

          @if (showsStoredSesIdentity && config?.ses && !isVerified) {
            <ix-typography format="h5" class="mt-4" id="emailSenderSesInstructionsHeader">
              {{ 'organizations.settings.email_sender.ses.instructions_header' | translate }}
            </ix-typography>
            <ix-typography format="body" class="d-block mt-2" id="emailSenderSesDomainInstructions">
              {{
                'organizations.settings.email_sender.ses.domain_instructions'
                  | translate: {identity: config?.ses?.identity}
              }}
            </ix-typography>
            <table
              class="table table-sm mt-2 dns-records"
              id="emailSenderSesDnsRecords"
              [attr.aria-label]="'organizations.settings.email_sender.ses.dns.caption' | translate"
            >
              <caption class="visually-hidden">
                {{ 'organizations.settings.email_sender.ses.dns.caption' | translate }}
              </caption>
              <thead>
                <tr>
                  <th>{{ 'organizations.settings.email_sender.ses.dns.name' | translate }}</th>
                  <th>{{ 'organizations.settings.email_sender.ses.dns.type' | translate }}</th>
                  <th>{{ 'organizations.settings.email_sender.ses.dns.value' | translate }}</th>
                </tr>
              </thead>
              <tbody>
                @for (record of config?.ses?.dnsRecords ?? []; track record.name) {
                  <tr>
                    <td><code>{{ record.name }}</code></td>
                    <td>{{ record.type }}</td>
                    <td><code>{{ record.value }}</code></td>
                  </tr>
                }
              </tbody>
            </table>
            <ix-button
              id="emailSenderVerifyButton"
              variant="secondary"
              class="mt-2"
              onkeypress=""
              [loading]="verifying"
              [disabled]="saving || testing"
              (click)="onVerifyClick()"
            >
              {{ 'organizations.settings.email_sender.ses.check_verification' | translate }}
            </ix-button>
          }
        }
      </form>

      <div class="col-md-12 input-field-size-max mt-4">
        <ix-modal-footer>
          <ix-button
            id="emailSenderCancelButton"
            variant="secondary"
            onkeypress=""
            [disabled]="!canCancel"
            (click)="onCancelClick()"
          >
            {{ 'general.cancel' | translate }}
          </ix-button>
          <ix-button
            id="emailSenderTestButton"
            variant="subtle-primary"
            onkeypress=""
            [loading]="testing"
            [disabled]="!form.valid || saving || verifying || !canSendTest"
            (click)="onSendTestClick()"
          >
            {{ 'organizations.settings.email_sender.send_test' | translate }}
          </ix-button>
          <ix-button
            id="emailSenderSaveButton"
            variant="primary"
            onkeypress=""
            [loading]="saving"
            [disabled]="!form.valid || testing || verifying"
            (click)="onSaveClick()"
          >
            {{ 'general.save' | translate }}
          </ix-button>
        </ix-modal-footer>
      </div>
    }
  </div>
}

<ng-template #disableModal let-modal>
  <ix-modal>
    <ix-modal-header id="emailSenderDisableModalHeader">
      {{ 'organizations.settings.email_sender.disable_header' | translate }}
    </ix-modal-header>
    <ix-modal-content id="emailSenderDisableModalContent">
      {{ 'organizations.settings.email_sender.disable_content' | translate }}
    </ix-modal-content>
    <ix-modal-footer>
      <ix-button
        id="emailSenderDisableModalYesButton"
        class="dismiss-modal"
        variant="secondary"
        onkeypress=""
        (click)="modal.close(true)"
      >
        {{ 'general.yes' | translate }}
      </ix-button>
      <ix-button
        id="emailSenderDisableModalNoButton"
        class="close-modal"
        variant="primary"
        onkeypress=""
        (click)="modal.dismiss()"
      >
        {{ 'general.no' | translate }}
      </ix-button>
    </ix-modal-footer>
  </ix-modal>
</ng-template>
